import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import CustomizedTabs from "../../../../shared/components/navigations/tabs";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";

// Tabs
import TransactionCertificate from "./tc-tabs/transaction-cetificate";
import SellerCertifiedProducts from "./tc-tabs/buyer-seller/seller-certfied-products";
import Shipments from "./tc-tabs/shipments/shipments";
import CertifiedProductsComponents from "./tc-tabs/certified-products";
import ComponentDetails from "./tc-tabs/component-details";
import TcStandardWeightInfo from "./tc-tabs/tc-standard-weight-info";
import IncomingTcDetails from "./tc-tabs/incoming-tc-details/index";
import DeclarationByCertificateBody from "./tc-tabs/declaration-by-certificate-body";

const TcForm = () => {
  const { formtype, TE_ID, currentTab } = useParams<{
    formtype: string;
    TE_ID: string;
    currentTab: string;
  }>();
  const baseUrl = formtype || "create-tc";
  const navigate = useNavigate();

  const enableDisableLogic: boolean = formtype === "submited-tc" ? false : true;

  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>(
    {}
  );
  const [tcStatus, setTcStatus] = useState<string>("");

  const { executeGet } = useApi();

  // Order must match API keys and UX sequence exactly
  // Use the same IDs used by the child tab components (v4 routes)
 const mandatoryTabs = [
    "base-details", // transaction-certificate
    "buyer-seller-products",
    "shipment-details",
    "product-details",
    "component-details",
    "incoming-tc-details",
    "certified-standard-weight",
    "declaration-submit-details",
  ];

  const fetchCompletionData = async () => {
    if (!TE_ID) return {};
    try {
      const response: any = await executeGet(TCAppConfig.GET_DISABLE_V4_TABS, {
        tcId: TE_ID,
      });

      const map = response.data || {};
      setCompletionMap(map);

      if (map["tc-status"]) {
        setTcStatus(map["tc-status"]);
      }
      return map;
    } catch (error) {
      console.error("Error fetching TC tab completion status:", error);
      return {};
    }
  };

  useEffect(() => {
    if (enableDisableLogic) fetchCompletionData();
  }, [TE_ID, enableDisableLogic]);

  const handleNextTab = async () => {
    await fetchCompletionData();
  };

  const isTabCompleted = (tabId: string) => !!completionMap[tabId];

  const isReadOnlyBasedOnStatus = () =>
    tcStatus === "S" || tcStatus === "W" || tcStatus === "E";

  const baseTabs = [
    {
      id: "base-details",
      label: "Transaction Information",
      component: <TransactionCertificate onNextTab={handleNextTab} />,
    },
    {
      id: "buyer-seller-products",
      label: "Buyer and Seller Information",
      component: <SellerCertifiedProducts onNextTab={handleNextTab} />,
    },
    {
      id: "shipment-details",
      label: "Shipment Information",
      component: <Shipments onNextTab={handleNextTab} />,
    },
    {
      id: "product-details",
      label: "Product Information",
      component: <CertifiedProductsComponents onNextTab={handleNextTab} />,
    },
    {
      id: "component-details",
      label: "Component Information",
      component: <ComponentDetails onNextTab={handleNextTab} />,
    },
    {
      id: "incoming-tc-details",
      label: "Incoming TC Information",
      component: <IncomingTcDetails onNextTab={handleNextTab} />,
    },
    {
      id: "certified-standard-weight",
      label: "Claims Category Certified Weight Information",
      component: <TcStandardWeightInfo onNextTab={handleNextTab} />,
    },
    {
      id: "declaration-submit-details",
      label: "Declarations",
      component: (
        <DeclarationByCertificateBody
          onNextTab={handleNextTab}
          completionMap={completionMap}
        />
      ),
    },
  ];

  const hasTE_ID = Boolean(TE_ID);

  const tabs = baseTabs.map((tab) => {
    // When disable logic isn't used, enable all
    if (!enableDisableLogic) return { ...tab, disabled: false };

    // If no TE_ID exists yet, only base-details is enabled
    if (!hasTE_ID) return { ...tab, disabled: tab.id !== "base-details" };

    // base-details always enabled
    if (tab.id === "base-details") return { ...tab, disabled: false };

    // For other tabs, enable only if previous mandatory tab completed
    const idx = mandatoryTabs.indexOf(tab.id);
    if (idx > 0) {
      const prev = mandatoryTabs[idx - 1];
      if (!isTabCompleted(prev)) return { ...tab, disabled: true };
    }

    return { ...tab, disabled: false };
  });

  return (
    <Box sx={{ mt: "2px" }}>
      <CustomizedTabs tabs={tabs} baseUrl={baseUrl} system="v4/tc" ID={TE_ID} />
    </Box>
  );
};

export default TcForm;
